// src/components/scenes/ItemDetailScene.js
// Filepath: // src/components/scenes/ItemDetailScene.js

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useScene } from "../../state/SceneContext";
import { setSceneHandlers } from "../../input/remote";
import { snapshotRowMemory, restoreRowMemory } from "../../input/remote";
import Hero from "../common/Hero";
import Focusable from "../common/Focusable";
import SideInfoDrawer from "../common/SideInfoDrawer";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const NAV_SAFE_LEFT = 24 + 72 + 32; // 128
const SHOW_INTERACTIVE_ACTIONS = false;

function debugEnabled() {
  try {
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("debugItem") === "1") return true;
      if (window.__KBEAN_DEBUG_ITEM === true) return true;
    }
  } catch { }
  return String(process.env.REACT_APP_DEBUG_ITEM || "").trim().toLowerCase() === "true";
}

export default function ItemDetailScene() {
  const { scene, setScene, pushScene, popScene, stack } = useScene();
  const { backTo, item: itemFromParams, id: idFromParams } = scene.params || {};

  // Snapshot Home/Grid row memory
  const homeRowMemRef = useRef(null);
  useEffect(() => {
    homeRowMemRef.current = snapshotRowMemory();
    return () => {
      if (homeRowMemRef.current) restoreRowMemory(homeRowMemRef.current);
    };
  }, []);

  // Accept normalized item or just an id
  const item = useMemo(
    () => (itemFromParams ?? (idFromParams ? { id: idFromParams } : {})),
    [itemFromParams, idFromParams]
  );

  const isVideo = item?.expectedMediaType === "Video" || item?.type === "Video";
  const isImage = item?.expectedMediaType === "Image" || item?.type === "Image";

  const videoSrc =
    (isVideo && (item?.mediaUrl || item?.videoUrl || scene?.params?.src)) || null;

  const previewSrc = isVideo ? (item?.previewUrl || scene?.params?.previewSrc || null) : null;

  // Metadata parity with Home
  const computedByline = useMemo(() => {
    if (item?.byline) return item.byline;
    const af = item?.artistFirstName ?? item?.artistFirst ?? item?._raw?.artistFirstName ?? null;
    const al = item?.artistLastName ?? item?.artistLast ?? item?._raw?.artistLastName ?? null;
    const yr = item?.year ?? item?.artYear ?? item?._raw?.year ?? item?._raw?.artYear ?? null;
    const name = [af, al].filter(Boolean).join(" ");
    return (name || yr) ? [name, yr].filter(Boolean).join(" • ") : null;
  }, [item]);

  const medium = item?.medium ?? item?._raw?.medium ?? null;
  const sharesYouOwn = Number((item?.sharesYouOwn ?? item?._raw?.sharesYouOwn ?? 0) || 0);
  const valueYouOwn = item?.valueYouOwn ?? item?._raw?.valueYouOwn ?? null;

  const badges = useMemo(() => [isVideo ? "Video" : null].filter(Boolean), [isVideo]);

  const ownershipLabel =
    sharesYouOwn > 0
      ? `You own ${sharesYouOwn} ${sharesYouOwn === 1 ? "share" : "shares"}${valueYouOwn ? ` ($${valueYouOwn})` : ""
      }`
      : undefined;

  const heroData = useMemo(
    () => ({
      id: item?.id ?? "unknown",
      title: item?.title ?? "Untitled",
      subtitle: item?.subtitle ?? "",
      backgroundUrl:
        item?.backgroundUrl ||
        item?.backdrop ||
        item?.thumbnailUrl ||
        item?.thumbnail ||
        "https://picsum.photos/seed/hero/1280/720",
      thumbnail:
        item?.imageUrl ||
        item?.thumbnailUrl ||
        item?.thumbnail ||
        "https://picsum.photos/seed/tn/800/450",
      videoUrl: videoSrc || undefined,
      previewUrl: previewSrc || undefined,
      type: isVideo ? "video" : isImage ? "image" : "unknown",
      byline: computedByline || undefined,
      meta: medium || undefined,
      badges,
      ownershipLabel,
    }),
    [item, videoSrc, previewSrc, isVideo, isImage, computedByline, medium, badges, ownershipLabel]
  );

  useEffect(() => {
    if (!debugEnabled()) return;
    console.groupCollapsed("[ItemDetailScene] received item + hero metadata");
    try {
      console.log("item keys:", Object.keys(item || {}));
      console.log("item:", item);
      console.log("heroData:", heroData);
    } finally {
      console.groupEnd();
    }
  }, [item, heroData]);

  // UI state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inWatchlist, setInWatchlist] = useState(false);
  // eslint-disable-next-line
  const [votes, setVotes] = useState({ up: 0, down: 0 });

  // Focus restore after drawer
  const lastFocusRef = useRef({ row: 10, col: 2 });

  const getCurrentFocusedRowCol = useCallback(() => {
    const el =
      document.activeElement?.closest?.('[data-focusable="true"]') ||
      document.querySelector('[data-focusable="true"].focused');
    const row = Number(el?.dataset?.row);
    const col = Number(el?.dataset?.col);
    return Number.isFinite(row) && Number.isFinite(col) ? { row, col } : null;
  }, []);

  function focusByRowCol(row, col) {
    const target = document.querySelector(
      `[data-row="${row}"][data-col="${col}"][data-focusable="true"]`
    );
    if (target) {
      document.querySelectorAll(".focused").forEach((n) => n.classList.remove("focused"));
      if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
      target.classList.add("focused");
      target.focus?.({ preventScroll: true });
      target.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
    }
  }

  const closeDrawerAndRestoreFocus = useCallback(() => {
    setDrawerOpen(false);
    const { row, col } = lastFocusRef.current || { row: 10, col: 2 };
    setTimeout(() => focusByRowCol(row, col), 0);
  }, []);

  // Drawer scrolling
  const drawerScrollRef = useRef(null);
  const scrollDrawerBy = useCallback((delta) => {
    const el = drawerScrollRef.current;
    if (!el) return false;
    const prev = el.scrollTop;
    el.scrollTo({ top: prev + delta, behavior: "smooth" });
    return true;
  }, []);

  // DPAD handlers
  useEffect(() => {
    setSceneHandlers({
      onEnter: () => {
        if (drawerOpen) {
          closeDrawerAndRestoreFocus();
          return true;
        }
        return false;
      },
      onBack: () => {
        if (drawerOpen) {
          closeDrawerAndRestoreFocus();
          return true;
        }

        // Restore row/col memory
        if (homeRowMemRef.current) restoreRowMemory(homeRowMemRef.current);

        // 1) Prefer explicit backTo snapshot from GridScene
        if (backTo && typeof backTo === "object" && backTo.name) {
          setScene({ name: backTo.name, params: backTo.params || {} });
          return true;
        }

        // 2) If we have a prior scene on the stack, pop back
        const prev = Array.isArray(stack) && stack.length > 1 ? stack[stack.length - 2] : null;
        if (prev?.name) {
          popScene?.();
          return true;
        }

        // 3) Fallback to home
        setScene({ name: "home", params: {} });
        return true;
      },
      onLeft: () => {
        if (drawerOpen) {
          closeDrawerAndRestoreFocus();
          return true;
        }
        return false;
      },
      onUp: () => {
        if (drawerOpen) return scrollDrawerBy(-160);
        return false;
      },
      onDown: () => {
        if (drawerOpen) return scrollDrawerBy(+160);
        return false;
      },
    });
    return () => setSceneHandlers({});
  }, [
    drawerOpen,
    closeDrawerAndRestoreFocus,
    popScene,
    setScene,
    stack,
    scrollDrawerBy,
    backTo,
  ]);

  const handleOpenDetails = useCallback(() => {
    const rc = getCurrentFocusedRowCol();
    if (rc) lastFocusRef.current = rc;
    setDrawerOpen(true);
    setTimeout(() => {
      const el = drawerScrollRef.current;
      if (el) {
        el.setAttribute("tabindex", "-1");
        el.focus({ preventScroll: true });
      }
    }, 0);
  }, [getCurrentFocusedRowCol]);

  // Routing helpers
  const openPlayer = useCallback(() => {
    const backToItem = { name: "item", params: { id: heroData.id, item, backTo } };
    (pushScene || setScene)({
      name: "player",
      params: {
        id: heroData.id,
        src: heroData.videoUrl,
        autoplay: true,
        backTo: backToItem,
      },
    });
  }, [pushScene, setScene, heroData.id, heroData.videoUrl, item, backTo]);

  const openPreview = useCallback(() => {
    const backToItem = { name: "item", params: { id: heroData.id, item, backTo } };
    (pushScene || setScene)({
      name: "player",
      params: {
        id: heroData.id,
        src: heroData.previewUrl || heroData.videoUrl,
        preview: true,
        autoplay: true,
        muted: true,
        backTo: backToItem,
      },
    });
  }, [pushScene, setScene, heroData.id, heroData.previewUrl, heroData.videoUrl, item, backTo]);

  const openGallerySingle = useCallback(() => {
    const backToItem = { name: "item", params: { id: heroData.id, item, backTo } };
    const items = [item];
    (pushScene || setScene)({
      name: "gallery",
      params: { items, startIndex: 0, backTo: backToItem },
    });
  }, [pushScene, setScene, heroData.id, item, backTo]);

  const openPrimary = useCallback(() => {
    if (isImage) return openGallerySingle();
    return openPlayer();
  }, [isImage, openGallerySingle, openPlayer]);

  const roundIconButtonSx = (theme) => ({
    width: 56,
    height: 56,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    color: "#ffffff",
    backgroundColor: "#000000",
    border: "3px solid #ffffff",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#000", border: "3px solid #fff", boxShadow: "none" },
    "&.Mui-focusVisible, &.focused, .focused &": {
      backgroundColor: theme.palette.primary.main,
      color: "#ffffff",
      border: "3px solid #ffffff",
      boxShadow: "none",
    },
    "&:focus, &:focus-visible": { outline: "none", boxShadow: "none" },
  });

  const detailImageUrl =
    item?.imageUrl || heroData.thumbnail || heroData.backgroundUrl;

  const fullDescription =
    item?.description || item?.subtitle || item?._raw?.artDescription || "";

  return (
    <Box
      className="scene"
      sx={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateRows: "auto 1fr",
        gap: 2,
        p: 2,
        position: "relative",
        isolation: "isolate",
      }}
      data-rails-root
      data-scope="rails"
    >
      <Box sx={{ position: "relative" }}>
        <Hero
          title={heroData.title}
          subtitle={heroData.subtitle}
          backgroundUrl={heroData.backgroundUrl}
          gradientStops="linear-gradient(135deg, rgba(0,0,0,0.85) 12%, rgba(0,0,0,0.55) 40%, rgba(0,0,0,0.2) 70%, rgba(0,0,0,0) 100%)"
          textTop="14vh"
          textLeft={NAV_SAFE_LEFT}
          byline={heroData.byline}
          meta={heroData.meta}
          badges={heroData.badges}
          ownershipLabel={heroData.ownershipLabel}
        />

        {/* Actions */}
        <Box
          sx={{
            position: "absolute",
            top: "400px",
            left: 128,
            right: 32,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            zIndex: 2,
          }}
        >
          <Focusable row={10} col={0} autoFocus>
            <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={openPrimary}>
              {isVideo ? "Watch Video" : "View Image"}
            </Button>
          </Focusable>

          {isVideo && (
            <Focusable row={10} col={1}>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={openPreview}
                sx={{ fontWeight: 800, minWidth: 140, border: "3px solid white" }}
              >
                Preview
              </Button>
            </Focusable>
          )}

          <Focusable row={10} col={2}>
            <Button
              variant="outlined"
              startIcon={<InfoOutlinedIcon />}
              onClick={handleOpenDetails}
              sx={{ fontWeight: 800, border: "3px solid white" }}
            >
              View details
            </Button>
          </Focusable>

          {SHOW_INTERACTIVE_ACTIONS && (
            <>
              <Box sx={{ width: 8 }} />
              <Focusable row={10} col={3}>
                <Tooltip title={inWatchlist ? "Remove from Watchlist" : "Add to Watchlist"}>
                  <IconButton onClick={() => setInWatchlist((v) => !v)} sx={roundIconButtonSx}>
                    {inWatchlist ? <RemoveIcon /> : <AddIcon />}
                  </IconButton>
                </Tooltip>
              </Focusable>
              <Focusable row={10} col={4}>
                <Tooltip title="Thumbs up">
                  <IconButton
                    onClick={() => setVotes((v) => ({ ...v, up: v.up + 1 }))}
                    sx={roundIconButtonSx}
                  >
                    <ThumbUpIcon />
                  </IconButton>
                </Tooltip>
              </Focusable>
              <Focusable row={10} col={5}>
                <Tooltip title="Thumbs down">
                  <IconButton
                    onClick={() => setVotes((v) => ({ ...v, down: v.down + 1 }))}
                    sx={roundIconButtonSx}
                  >
                    <ThumbDownIcon />
                  </IconButton>
                </Tooltip>
              </Focusable>
            </>
          )}
        </Box>

        {/* Preview image */}
        <Box
          sx={{
            position: "absolute",
            top: "520px",
            left: 128,
            width: 500,
            borderRadius: "20px",
            border: "3px solid #ffffff",
            overflow: "hidden",
            zIndex: 2,
            backgroundColor: "black",
          }}
        >
          <Box sx={{ pt: "56.25%" }} />
          <Box
            component="img"
            src={detailImageUrl}
            alt=""
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              transform: "translateZ(0)",
            }}
          />
        </Box>
      </Box>

      <Card sx={{ alignSelf: "stretch" }}>
        <CardContent>
          <Typography variant="h2">{heroData.title}</Typography>
          {!!heroData.subtitle && (
            <Typography sx={{ color: "text.secondary", mt: 0.5 }}>
              {heroData.subtitle}
            </Typography>
          )}
          {isVideo ? (
            <Typography sx={{ mt: 1 }}>
              <strong>Media:</strong> {heroData.videoUrl ? "OK" : "Missing"} {heroData.videoUrl ?? ""}
              {heroData.previewUrl ? " • preview: OK" : ""}
            </Typography>
          ) : isImage ? (
            <Typography sx={{ mt: 1 }}>
              <strong>Type:</strong> Image
            </Typography>
          ) : (
            <Typography sx={{ mt: 1 }}>
              <strong>Type:</strong> Unknown
            </Typography>
          )}

          {(heroData.byline || heroData.meta || heroData.ownershipLabel) && (
            <Box sx={{ mt: 1.5 }}>
              {heroData.byline && (
                <Typography sx={{ color: "text.primary" }}>{heroData.byline}</Typography>
              )}
              {heroData.meta && (
                <Typography sx={{ color: "text.secondary" }}>{heroData.meta}</Typography>
              )}
              {heroData.ownershipLabel && (
                <Typography sx={{ color: "success.main", fontWeight: 600 }}>
                  {heroData.ownershipLabel}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Drawer with scrollable details */}
      <SideInfoDrawer
        open={drawerOpen}
        onClose={closeDrawerAndRestoreFocus}
        anchor="right"
        width={900}
        title={heroData.title}
        subtitle={heroData.byline}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            borderRadius: "10px",
            overflow: "hidden",
            bgcolor: "grey.900",
          }}
        >
          <Box sx={{ pt: "56.25%" }} />
          <Box
            component="img"
            src={detailImageUrl}
            alt=""
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              transform: "translateZ(0)",
            }}
          />
        </Box>
        
        {!!fullDescription && (
          <Box>
            <Typography variant="h2">
              Description
            </Typography>
          </Box>
        )}

        {/* SCROLLABLE: metadata + description */}
        <Box
          ref={drawerScrollRef}
          tabIndex={-1}
          sx={{
            maxHeight: "42vh",
            overflowY: "auto",
            pr: 1,
            borderRadius: 1,
            outline: "none",
          }}
        >
          {!!fullDescription && (
            <Box sx={{ mb: 1.5 }}>

              <Typography
                sx={{
                  color: "text.secondary",
                  whiteSpace: "pre-wrap",
                  fontSize: "1.5rem",
                  lineHeight: 1.65,
                }}
              >
                {fullDescription}
              </Typography>
            </Box>
          )}
        </Box>
      </SideInfoDrawer>
    </Box>
  );
}
